import { useState, useEffect, useCallback } from 'react';

export function useFormPersistence<T extends Record<string, any>>(key: string, initialValues: T) {
  const [formData, setFormData] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValues;
    try {
      const saved = sessionStorage.getItem(key);
      return saved ? { ...initialValues, ...JSON.parse(saved) } : initialValues;
    } catch (error) {
      return initialValues;
    }
  });

  // Save to sessionStorage whenever formData changes
  useEffect(() => {
    if (typeof window !== 'undefined' && formData) {
      try {
        // Only save fields that exist in initialValues
        const dataToSave: Partial<T> = {};
        Object.keys(initialValues).forEach(key => {
          if (key in formData) {
            dataToSave[key as keyof T] = formData[key as keyof T];
          }
        });
        sessionStorage.setItem(key, JSON.stringify(dataToSave));
      } catch (error) {
        // Silently fail - form should still work without persistence
      }
    }
  }, [key, formData, initialValues]);

  // Clear saved data
  const clearFormData = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(key);
      setFormData(initialValues);
    }
  }, [key, initialValues]);

  // Update form data
  const updateFormData = useCallback((newData: Partial<T> | ((prev: T) => T)) => {
    setFormData(prev => {
      const nextData = typeof newData === 'function' 
        ? newData(prev) 
        : { ...prev, ...newData };
      return nextData;
    });
  }, []);

  return {
    formData,
    setFormData: updateFormData,
    clearFormData,
    resetFormData: clearFormData
  };
}
